import { useState, useMemo, useCallback, useRef } from "react";
import {
  Text,
  ActionButton,
  Button,
  TextArea,
  MenuTrigger,
  Popover,
  Form,
  Slider,
  Divider,
  Picker,
  PickerItem,
} from "@react-spectrum/s2";
import { useControlledState } from "@react-stately/utils";
// import { RichTextArea } from "@quarry-s2/rich-text-area";
import PropertiesIcon from "@react-spectrum/s2/icons/Properties";
import ChevronRightIcon from "icon:@app/assets/S2_ChevronSize100.svg";
import { AudioPlayer } from "@app/components/AudioPlayer";
import { VoicesList } from "@app/components/VoicesList";
import { TTS_MODEL_OPTIONS, DEFAULT_TTS_MODEL } from "@app/hooks/textToSpeech";

// import { PronounceNode } from "./lexical/nodes/PronounceNode";
// import { AudioToolbar } from "./lexical/plugins/AudioToolbar";

import {
  style,
  iconStyle,
} from "@react-spectrum/s2/style" with { type: "macro" };

const getPitchLabel = (pitch) => {
  if (pitch < -1) return "Low";
  if (pitch > 1) return "High";
  return "Neutral";
};

const MAX_CHARACTERS = 7500;

export const TextToSpeech = (props) => {
  const {
    audioUrl,
    voices = [],

    segments = [],

    models = TTS_MODEL_OPTIONS,

    onGenerate,
    isPending,
    isDisabled,
    isInvalid,

    maxCharacters = MAX_CHARACTERS,

    onReady,
    onPreviewPlay,

    size = "M",

    styles = "",

    children,
  } = props;
  const hasModelSelect =
    props.model != null ||
    props.defaultModel != null ||
    typeof props.onModelChange === "function";
  const [model, setModel] = useControlledState(
    props.model,
    props.defaultModel ?? DEFAULT_TTS_MODEL,
    props.onModelChange,
  );
  const [voiceId, setVoiceId] = useControlledState(
    props.voiceId,
    props.defaultVoiceId,
    props.onVoiceSelect,
  );
  const [text, setText] = useControlledState(
    props.text,
    props.defaultText,
    props.onTextChange,
  );
  const [speed, setSpeed] = useControlledState(
    props.speed,
    props.defaultSpeed,
    props.onSpeedChange,
  );
  const [pitch, setPitch] = useControlledState(
    props.pitch,
    props.defaultPitch,
    props.onPitchChange,
  );

  const selectedVoice = useMemo(
    () => voices?.find((voice) => voice.voiceId === voiceId),
    [voiceId, voices],
  );

  const handleTextChange = useCallback(
    (value) => {
      const newText = value.slice(0, maxCharacters).replace(/\s{2,}\n/g, " \n");

      setText(newText);
    },
    [maxCharacters],
  );

  const hasText = typeof text === "string";

  return (
    <div className={style({ position: "relative" }) + styles}>
      {/* <RichTextArea
        aria-label="Text to speech input"
        placeholder="Start typing or paste text here…"
        handleChange={(textValue, htmlValue) => {
          // handleTextChange(textValue);
          console.log(htmlValue);
        }}
        hideToolbar
        initialContent={text || _text}
        configOverrides={{
          nodes: [PronounceNode],
        }}
        customPlugins={[
          () => (
            <AudioToolbar
              audioUrl={audioUrl}
              voices={voices}
              text={text || _text}
              onTextChange={handleTextChange}
              onGenerate={onGenerate}
              isPending={isPending}
              onVoiceSelect={handleVoiceSelect}
              maxCharacters={maxCharacters}
            >
              {children}
            </AudioToolbar>
          ),
        ]}
        styles={style({ paddingBottom: 32 })}
      /> */}
      {hasText && (
        <TextArea
          // {...inputProps}
          aria-label="Text to speech input"
          placeholder="Start typing or paste text here…"
          value={text}
          onChange={handleTextChange}
          isDisabled={isDisabled}
          // isInvalid={isInvalid}
          size={size}
          data-custom-color={isInvalid}
          UNSAFE_className={
            "__PromptInputContainer __PromptInput" +
            style({
              "--borderColor": {
                type: "color",
                value: {
                  default: "transparent",
                  isInvalid: "notice-700",
                },
              },
            })({ isInvalid })
          }
        />
      )}
      <div
        className={style({
          boxSizing: "border-box",
          padding: 8,
          backgroundColor: "elevated",
          borderRadius: "default",
          boxShadow: "elevated",

          // marginY: 24,

          display: "flex",
          flexDirection: "column",
          gap: 12,

          margin: {
            isAbsolute: 12,
            default: 0,
          },

          position: {
            isAbsolute: "absolute",
            default: "relative",
          },
          bottom: {
            default: 0,
            isAbsolute: 8,
          },
          insetStart: {
            default: 0,
            isAbsolute: 8,
          },
          insetEnd: {
            default: 0,
            isAbsolute: 8,
          },
        })({ isAbsolute: hasText })}
      >
        {(!!audioUrl || isPending) && (
          <>
            <AudioPlayer
              url={audioUrl}
              isAudioJobScheduled={isPending}
              isEditorEmpty={!audioUrl}
              segments={segments}
              onReady={onReady}
              onPreviewPlay={onPreviewPlay}
            />
            <Divider orientation="horizontal" size="S" />
          </>
        )}
        <div
          className={style({ display: "flex", alignItems: "center", gap: 12 })}
        >
          <MenuTrigger>
            <ActionButton aria-label="Voice settings" isQuiet>
              <PropertiesIcon />
            </ActionButton>
            <Popover>
              <div
                className={style({
                  paddingTop: 4,
                  display: "flex",
                  flexDirection: "column",
                  gap: 12,
                })}
              >
                <Form styles={style({ marginX: 12 })}>
                  {hasModelSelect && (
                    <Picker
                      label="Model"
                      selectedKey={model}
                      onSelectionChange={setModel}
                      isDisabled={isDisabled || isPending}
                    >
                      {models.map((option) => (
                        <PickerItem key={option.id} id={option.id}>
                          {option.label}
                        </PickerItem>
                      ))}
                    </Picker>
                  )}
                  <Slider
                    label="Speed"
                    value={speed}
                    onChange={setSpeed}
                    minValue={0.7}
                    maxValue={1.5}
                    step={0.05}
                    formatOptions={{ minimumFractionDigits: 2 }}
                  />
                  <Slider
                    label="Pitch"
                    value={pitch}
                    onChange={setPitch}
                    minValue={-3}
                    maxValue={3}
                    step={1}
                  />
                </Form>
                {!!voices?.length && (
                  <>
                    <Divider styles={style({ marginX: 12 })} />
                    <MenuTrigger>
                      <ActionButton
                        textValue="Voices"
                        isQuiet
                        styles={style({ width: "full" })}
                        UNSAFE_style={{ justifyContent: "stretch" }}
                      >
                        <Text
                          styles={style({
                            gridArea: "icon",
                            justifySelf: "start",
                          })}
                        >
                          Voices
                        </Text>
                        <div
                          className={style({
                            gridArea: "text",
                            justifySelf: "end",
                            display: "flex",
                            gap: 8,
                          })}
                        >
                          <Text styles={style({ font: "ui" })}>
                            {selectedVoice?.displayName}
                          </Text>
                          <ChevronRightIcon
                            slot={null}
                            styles={iconStyle({ gridArea: "", size: "S" })}
                            UNSAFE_style={{ width: 10, height: 10 }}
                          />
                        </div>
                      </ActionButton>
                      <Popover
                        placement="end top"
                        offset={0}
                        crossOffset={-8}
                        hideArrow
                      >
                        <div
                          className={style({
                            width: 320,
                          })}
                        >
                          <VoicesList
                            voices={voices}
                            voiceId={voiceId}
                            onVoiceSelect={setVoiceId}
                            isQuiet
                            selectionStyle="checkmark"
                            disallowEmptySelection
                          />
                        </div>
                      </Popover>
                    </MenuTrigger>
                  </>
                )}
              </div>
            </Popover>
          </MenuTrigger>
          {children}
          <Text
            styles={style({
              marginStart: "auto",
              font: "ui-sm",
              color: "neutral-subdued",
            })}
          >
            {hasText ? `${text?.length ?? 0}/${maxCharacters}` : ""}
          </Text>
          <Button
            variant="accent"
            isDisabled={
              (hasText && !text?.trim()) || isPending || isDisabled /* ||
                      (text === generateSpeech.variables?.text &&
                        selectedVoice.voiceId ===
                          generateSpeech.variables?.voiceId) */
            }
            onPress={() =>
              onGenerate?.({
                text: text,
                voiceId: selectedVoice?.voiceId ?? voiceId,
                voiceConfig: {
                  speed: speed,
                  pitch: pitch,
                  ...(hasModelSelect ? { model } : {}),
                },
              })
            }
          >
            <Text>{!!audioUrl ? "Generate again" : "Generate"}</Text>
          </Button>
        </div>
      </div>
    </div>
  );
};
